export class CartPageRecommendations extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    this.init()

    // TODO:
    // - The product is added to the cart, but the cart page is not updated.
    // - The function addItemFromForm, is used to add the product to the cart, maybe we need to make a different function for cart page.
  }

  async init () {
    this.initializeState()
    await this.fetchInitialCartState()
    this.scheduleRender()
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.resetState()
  }

  setupListeners () {
    super.setupListeners()

    this.onCartChanged = this.handleCartChange.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChanged,
    )
  }

  initializeState () {
    this.isRendering = false
    this._expanded = false
    this.renderTimeoutId = null
    this.cartState = null
    this.lastRecommendationProductId = null
  }

  resetState () {
    this.expanded = false
    this.isRendering = false
    this.cartState = null
    this.lastRecommendationProductId = null
    this.clearRenderTimeout()
  }

  clearRenderTimeout () {
    if (this.renderTimeoutId) {
      window.clearTimeout(this.renderTimeoutId)
      this.renderTimeoutId = null
    }
  }

  get expanded () {
    return this._expanded
  }

  set expanded (expanded) {
    this._expanded = expanded

    const aside = document.querySelector('dialog[key="cart"] .drw-Drawer_Aside')
    const recs = document.querySelectorAll('cart-page-recommendations')

    this.updateExpandedState(aside, recs, expanded)
  }

  async fetchInitialCartState () {
    try {
      const response = await fetch('/cart.js')
      const cartState = await response.json()
      this.cartState = cartState
      this.lastRecommendationProductId = null // Reset so first render will work
    } catch (error) {
      console.error('Failed to fetch initial cart state:', error)
    }
  }

  getLastAddedItem (cartState) {
    if (!this.hasCartItems(cartState)) {
      return null
    }

    // Sort by key (higher key = more recent) and return the first item
    const sortedItems = [...cartState.items].sort((a, b) => b.key - a.key)
    return sortedItems[0]
  }

  hasCartItems (cartState) {
    return cartState?.items && cartState.items.length > 0
  }

  hasValidCartData () {
    return this.hasCartItems(this.cartState)
  }

  shouldUpdateRecommendations (cartState) {
    const lastAddedItem = this.getLastAddedItem(cartState)
    const currentProductId = lastAddedItem?.product_id

    // Update if the last added product has changed
    const shouldUpdate = currentProductId && currentProductId !== this.lastRecommendationProductId

    if (shouldUpdate) {
      this.lastRecommendationProductId = currentProductId
    }

    return shouldUpdate
  }

  buildRecommendationUrl (productId, intent) {
    const baseUrl = this.dataset.url
    if (!baseUrl || !productId) return null

    // Replace the product_id parameter or add it if missing
    // eslint-disable-next-line compat/compat
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set('product_id', productId)
    if (intent) {
      url.searchParams.set('intent', intent)
    }
    return url.toString()
  }

  async fetchRecommendationHtml (url) {
    const response = await fetch(url)
    return response.text()
  }

  parseRecommendationHtml (htmlString) {
    const html = document.createElement('div')
    html.innerHTML = htmlString
    return html
  }

  hasRecommendationItems (htmlElement) {
    return htmlElement.querySelectorAll('[data-rec-item]').length > 0
  }

  extractRecommendationContent (htmlElement) {
    const recommendationElement = htmlElement.querySelector('cart-page-recommendations')
    return recommendationElement ? recommendationElement.innerHTML : ''
  }

  async fetchRecommendationsWithFallback (productId) {
    try {
      // Try complementary recommendations first
      const complementaryContent = await this.tryFetchRecommendations(productId, 'complementary')
      if (complementaryContent) return complementaryContent

      // Fallback to related recommendations
      const relatedContent = await this.tryFetchRecommendations(productId, 'related')
      return relatedContent || ''
    } catch (error) {
      console.error('Recommendation fetch error:', error)
      return ''
    }
  }

  async tryFetchRecommendations (productId, intent) {
    const url = this.buildRecommendationUrl(productId, intent)
    if (!url) return null

    const html = await this.fetchRecommendationHtml(url)
    const element = this.parseRecommendationHtml(html)

    if (this.hasRecommendationItems(element)) {
      return this.extractRecommendationContent(element)
    }

    return null
  }

  scheduleRender () {
    // Cancel any existing timeout to prevent multiple renders
    this.clearRenderTimeout()

    if (!this.isRendering) {
      this.renderTimeoutId = window.setTimeout(() => {
        this.renderTimeoutId = null
        this.render()
      }, 500)
    }
  }

  async render () {
    if (this.isRendering) return
    this.isRendering = true

    try {
      if (!this.canRenderRecommendations()) {
        this.expanded = false
        return
      }

      const lastAddedItem = this.getLastAddedItem(this.cartState)
      const content = await this.fetchRecommendationsWithFallback(lastAddedItem.product_id)

      this.updateRecommendationsDisplay(content)
    } catch (error) {
      console.error('Render error:', error)
      this.expanded = false
    } finally {
      this.isRendering = false
    }
  }

  canRenderRecommendations () {
    if (!this.hasValidCartData()) return false

    const lastAddedItem = this.getLastAddedItem(this.cartState)
    return lastAddedItem?.product_id
  }

  updateRecommendationsDisplay (content) {
    if (content) {
      this.updateRecommendationContainers(content)
      this.externalEls?.container?.element.classList.remove('crt-Recommendations-hidden')
      this.expanded = true
    } else {
      this.expanded = false
    }
  }

  updateRecommendationContainers (content) {
    document
      .querySelectorAll('cart-page-recommendations')
      .forEach((container) => {
        container.innerHTML = content
      })
  }

  updateExpandedState (aside, recs, expanded) {
    aside?.setAttribute('aria-expanded', expanded)

    recs?.forEach((rec) => {
      rec.setAttribute('loaded', expanded)
    })
  }

  handleCartChange (event) {
    const { state } = event.detail
    if (!state) return

    // Store the new cart state
    this.cartState = state

    // Only render if recommendations should update
    if (this.shouldUpdateRecommendations(state)) {
      this.scheduleRender()
    }
  }
}

if (!customElements.get('cart-page-recommendations')) {
  customElements.define('cart-page-recommendations', CartPageRecommendations)
}

const { setInitialised, useArrows } = window.BAO.Carousel.plugins

class CartPageRecommendationsCarousel extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    window.setTimeout(() => this.render(), 500)
  }

  render () {
    if (!this.els?.slide?.exists) return

    this.carousel = this.setupCarousel()
  }

  setupCarousel () {
    return new window.KeenSlider(
      this.els.slides.element,
      {
        selector: this.els.slide.selector,
        loop: false,
        mode: 'snap',
        slides: {
          perView: 2,
          spacing: 20,
        },
        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: 4,
              spacing: 5,
            },
          },
        },
      },
      [
        setInitialised(),
        useArrows({
          next: this.els.next.element,
          previous: this.els.prev.element,
        }),
      ],
    )
  }
}

if (!customElements.get('cart-page-recommendations-carousel')) {
  customElements.define(
    'cart-page-recommendations-carousel',
    CartPageRecommendationsCarousel,
  )
}
